import { Icon } from '@design-system';
import classNames from 'classnames';
import { useTranslation } from 'next-i18next';
import React from 'react';

import { DrawWithFrames } from '../../../features/draws/types';
import { usePreAuthDrawCtx } from '../../../providers/PreAuthDrawProvider';
import { CurrencyCode } from '../../../types/Locations';
import { DrawerFormType } from '../../../types/Purchasing';
import { framedByDefault } from '../../../utils/frames';
import { ProductPrice } from '../../ProductPrice/ProductPrice';

type Props = {
  disabled?: boolean;
  draw: DrawWithFrames;
  setVisibleForm?: (formType: DrawerFormType) => void;
  currencyCode?: CurrencyCode;
};

const FrameSelectorDraw: React.FC<Props> = ({
  currencyCode,
  disabled,
  draw,
  setVisibleForm,
}) => {
  const { t } = useTranslation('product');
  const { customerSelectedFrame } = usePreAuthDrawCtx();

  const { frames, product } = draw;

  const frameIncluded = framedByDefault(product.framingOffered);

  const getLabel = () => {
    if (frameIncluded && frames) {
      return frames[0].title;
    }
    if (customerSelectedFrame) {
      return customerSelectedFrame?.title;
    }
    if (!customerSelectedFrame) {
      return t('drawers.review.unframedTitle');
    }

    return undefined;
  };

  const totalPrice =
    customerSelectedFrame?.variant.consolidatedVariants.reduce(
      (acc, item) => acc + item.price * item.quantity,
      0,
    ) ?? 0;

  const frameHasUpgrade =
    (frames?.find((f) => f.title === customerSelectedFrame?.title)?.variants
      ?.length ?? 0) > 1;

  return (
    <button
      className={classNames(
        'flex w-full justify-between text-left transition duration-300 hover:text-neutral-4',
        {
          'cursor-not-allowed text-neutral-4': disabled,
        },
      )}
      disabled={disabled}
      onClick={() => {
        setVisibleForm?.('frame');
      }}
      type="button"
    >
      <span className="flex w-full flex-1 flex-col">
        <span>{getLabel()}</span>
        {frameHasUpgrade ? (
          <span className="text-neutral-4">
            {customerSelectedFrame?.variant.title}
          </span>
        ) : null}
      </span>
      <span className="flex shrink-0 gap-2">
        {totalPrice !== 0 && !frameIncluded && (
          <span>
            &#43;{' '}
            <ProductPrice currencyCode={currencyCode} price={totalPrice} />
          </span>
        )}
        {!customerSelectedFrame && frameIncluded && (
          <span className="text-neutral-4">
            {t('drawers.review.includedFrameState')}
          </span>
        )}
        <Icon name="ic_chevron_right" />
      </span>
    </button>
  );
};

export default FrameSelectorDraw;
